import {createStore, Store as ReduxStore} from 'redux'

import {TransitionAsyncState, UpdateStrategy} from './useTransition.types'

enum ActionTypes {
  PENDING = '@@async/pending',
  FULFILLED = '@@async/fulfilled',
  REJECTED = '@@async/rejected',
}

type PendingAction = {
  type: ActionTypes.PENDING
  isPending: boolean
}

type FulfilledAction = {
  type: ActionTypes.FULFILLED
  payload: any
  updateStrategy?: UpdateStrategy<any>
}

type RejectedAction = {
  type: ActionTypes.REJECTED
  error: unknown
}

type StoreKey = string
type Action = PendingAction | FulfilledAction | RejectedAction
type State = TransitionAsyncState<any, any>
type Store = ReduxStore<State, Action>

export type StoreParams = {
  key?: StoreKey
  isPending: boolean
  updateStrategy?: UpdateStrategy<any>
}

const identity: UpdateStrategy<any> = v => v
const generateStoreKey = (function () {
  let idx = 0

  return function _generateStoreKey(mayBeKey?: StoreKey): StoreKey {
    if (mayBeKey) {
      return mayBeKey
    }

    return `unknown_${++idx}`
  }
})()

function createReducer(initialState: State) {
  return function reducer(state: State = initialState, action: Action): State {
    switch (action.type) {
      case ActionTypes.PENDING: {
        if (state.isPending) {
          return state
        }

        return {
          ...state,
          isPending: action.isPending,
        }
      }
      case ActionTypes.FULFILLED: {
        const updateStrategy = action.updateStrategy || identity

        return {
          ...state,
          isPending: false,
          payload: updateStrategy(action.payload, state.payload),
          error: undefined,
        }
      }
      case ActionTypes.REJECTED: {
        return {
          ...state,
          isPending: false,
          error: action.error,
        }
      }
    }

    return state
  }
}

const storeCache: Record<StoreKey, Store> = {}
const getStoreFromCache = (mayBeKey?: StoreKey, isPending: boolean = false): Store => {
  const storeKey: StoreKey = generateStoreKey(mayBeKey)

  if (storeCache[storeKey] === undefined) {
    storeCache[storeKey] = createStore(createReducer({isPending}))
  }

  return storeCache[storeKey] as Store
}

export const getStore = (storeParams: StoreParams) => {
  const store = getStoreFromCache(storeParams.key, storeParams.isPending)
  const actions = {
    pending(isPending: boolean) {
      store.dispatch({
        type: ActionTypes.PENDING,
        isPending,
      })
    },
    resolve(payload: unknown) {
      store.dispatch({
        type: ActionTypes.FULFILLED,
        payload,
        updateStrategy: storeParams.updateStrategy,
      })
    },
    reject(error: unknown) {
      store.dispatch({
        type: ActionTypes.REJECTED,
        error,
      })
    },
  } as const

  return [store, actions] as const
}
